import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AttachSlot } from "@/components/SupplierPoSection";
import { StockReceiveDialog, STOCK_PO_STATUS, fmtQty } from "@/components/StockPoDialogs";
import { ArrowLeft, Loader2, FileText, ClipboardList, Send, Trash2, PackageCheck, Package, Ship, ExternalLink } from "lucide-react";

const STEPS = [["draft", "Draft"], ["requested", "Requested"], ["approved", "Approved"], ["in_transit", "In Transit"], ["partial", "Partly Received"], ["received", "Received"]];
const money = (cur, v) => `${cur || "USD"} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function RequestBar({ d, reload }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const req = d.request || {};
  const send = async () => {
    setBusy(true);
    try { await api.post(`/orders/stock/supplier-pos/${d.id}/request`, { note }); toast.success("Sent to Somcore Finance"); setNote(""); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (req.status === "issued") return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-dashed border-border pt-3" data-testid="stockpo-request-bar">
      {req.status === "rejected" && <span className="w-full text-xs text-red-600" data-testid="stockpo-rejected">Rejected by Somcore Finance{req.reject_reason ? ` — ${req.reject_reason}` : ""}. Fix and send again.</span>}
      {req.status === "approved" && <span className="text-xs font-semibold text-blue-700">Approved — Finance is issuing the PO.</span>}
      {req.status === "requested" && <span className="text-xs font-semibold text-amber-700">Waiting for Somcore Finance to review.</span>}
      {["none", "rejected", undefined].includes(req.status) && (
        <>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for Finance (optional)" className="h-9 max-w-xs" data-testid="stockpo-request-note" />
          <Button size="sm" onClick={send} disabled={busy} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="stockpo-request-btn">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}Request PO From Somcore Finance</Button>
        </>
      )}
    </div>
  );
}

export default function StockPoDetail({ d, reload }) {
  const nav = useNavigate();
  const [recv, setRecv] = useState(null);
  const st = d.stock_status;
  const [label, cls] = STOCK_PO_STATUS[st] || STOCK_PO_STATUS.draft;
  const activeIdx = STEPS.findIndex(([k]) => k === (st === "rejected" ? "requested" : st));
  const open = ["in_transit", "partial"].includes(st);
  const viewPo = async () => {
    try {
      const res = await api.get(`/orders/stock/supplier-pos/${d.id}/file/po`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" }));
      window.open(url, "_blank"); setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    if (!window.confirm("Delete this stock PO? This cannot be undone.")) return;
    try { await api.delete(`/orders/stock/supplier-pos/${d.id}`); toast.success("Stock PO deleted"); nav("/orders?section=somcore"); } catch (e) { toast.error(apiError(e)); }
  };
  const [shipBusy, setShipBusy] = useState(false);
  const createShipment = async () => {
    setShipBusy(true);
    try { const { data } = await api.post(`/catalog/stock/orders/${d.id}/shipment`); toast.success(`Shipment ${data.shipment_no} created`); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setShipBusy(false); }
  };
  const close = async () => {
    if (!window.confirm("Close this PO? Anything not received stops showing as In Transit.")) return;
    try { await api.post(`/catalog/stock/orders/${d.id}/close`); toast.success("PO closed"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <AppLayout title={d.po_file ? d.title : "Stock PO"}>
      <div className="mb-4 flex items-center justify-between">
        <button onClick={() => nav("/orders?section=somcore")} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800" data-testid="sompo-back"><ArrowLeft className="h-4 w-4" />Back to Somcore PO's</button>
        {!["partial", "received"].includes(st) && d.request?.status !== "issued" && <Button variant="outline" size="sm" onClick={del} data-testid="sompo-delete" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"><Trash2 className="mr-1.5 h-4 w-4" />Delete Stock PO</Button>}
      </div>

      <div className="mb-4 rounded-xl border border-border bg-white p-6" data-testid="sompo-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              <span className="rounded bg-[#FBF6E9] px-2 py-0.5 text-[#A87F31]" data-testid="sompo-title-pill">{d.po_file ? d.title : "Stock PO"}</span><span>Stock Purchase Order</span>
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="sompo-title">{d.supplier_name}</h1>
            <p className="mt-1 text-sm text-muted-foreground">For stock · Created by {d.created_by_name || "—"}{d.rfq_code && <> · From <button onClick={() => nav(`/rfqs/${d.rfq_id}`)} className="font-semibold text-[#1B4383] hover:underline" data-testid="stockpo-req-link">{d.rfq_code}</button></>}{d.expected_date ? ` · Expected ${d.expected_date}` : ""}{d.ref ? ` · Supplier ref ${d.ref}` : ""}
              {d.shipment_no && <> · <button onClick={() => nav(`/logistics/shipment/${d.shipment_id}`)} className="inline-flex items-center gap-1 font-semibold text-[#1B4383] hover:underline" data-testid="stockpo-shipment-link"><Ship className="h-3.5 w-3.5" />{d.shipment_no}<ExternalLink className="h-3 w-3 opacity-70" /></button></>}</p>
            {!["received", "closed"].includes(st) && !d.shipment_no && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={createShipment} disabled={shipBusy || d.request?.status !== "issued"} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="stockpo-create-shipment">{shipBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Ship className="mr-1.5 h-4 w-4" />}Create Shipment</Button>
                {d.request?.status !== "issued" && <span className="text-[11px] text-muted-foreground">Available once Finance issues the PO</span>}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">PO Value</div>
            <div className="font-heading text-2xl font-bold text-slate-900">{money(d.currency, d.total)}</div>
            <span className={`mt-1 inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`} data-testid="stockpo-status">{label}</span>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-1.5" data-testid="sompo-status">
          {STEPS.map(([k, lbl], i) => (
            <span key={k} className={`rounded-full px-3 py-1 text-xs font-medium ${i === activeIdx ? "bg-[#1B4383] text-white" : i < activeIdx ? "bg-[#E6ECF5] text-[#1B4383]" : "bg-slate-100 text-slate-400"}`}>{lbl}</span>
          ))}
          {st === "closed" && <span className="rounded-full bg-slate-700 px-3 py-1 text-xs font-medium text-white">Closed</span>}
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-border bg-white p-5" data-testid="sompo-docs">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><ClipboardList className="h-4 w-4" />Purchase Order & Supporting Documents</div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-lg border border-border bg-slate-50/40 p-3" data-testid="stockpo-issued-slot">
            <div className="min-w-0"><div className="text-xs font-semibold text-slate-700">Issued Somcore PO</div><div className="truncate text-[11px] text-muted-foreground">{d.po_file ? `${d.po_file.filename} · by ${d.request?.issued_by || "Finance"}` : "Somcore Finance uploads this once approved"}</div></div>
            {d.po_file && <Button size="sm" variant="outline" onClick={viewPo} data-testid="sompo-view-po" className="h-7 border-[#1B4383]/30 px-2 text-[#1B4383] hover:bg-[#E6ECF5]"><FileText className="mr-1 h-3 w-3" />View</Button>}
          </div>
          <AttachSlot orderId="stock" spoId={d.id} kind="supplier_quote" att={d.attachments?.supplier_quote} onChanged={reload} hideAuto />
        </div>
        <RequestBar d={d} reload={reload} />
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-white" data-testid="sompo-lines">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-slate-50 px-4 py-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Package className="h-4 w-4" />Items</span>
          {open && d.can_receive && (
            <div className="flex items-center gap-2">
              <button onClick={close} className="text-[11px] text-slate-500 hover:text-red-600" data-testid="stockpo-close">Close PO</button>
              <Button size="sm" onClick={() => setRecv(d)} className="h-8 bg-[#527D63] hover:bg-[#456a54]" data-testid="stockpo-receive"><PackageCheck className="mr-1.5 h-4 w-4" />Receive Stock</Button>
            </div>
          )}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-slate-500">
              <th className="px-4 py-2.5 font-medium">SKU</th><th className="px-4 py-2.5 font-medium">Description</th>
              <th className="px-4 py-2.5 text-right font-medium">Qty</th><th className="px-4 py-2.5 text-right font-medium">Unit Price</th>
              <th className="px-4 py-2.5 text-right font-medium">Received</th><th className="px-4 py-2.5 text-right font-medium">In Transit</th>
            </tr>
          </thead>
          <tbody>
            {d.lines.map((l, i) => (
              <tr key={l.product_id} className="border-b border-border last:border-0" data-testid={`sompo-line-${i}`}>
                <td className="px-4 py-2.5 font-mono text-xs font-semibold text-[#1B4383]">{l.sku}</td>
                <td className="px-4 py-2.5 text-slate-800">{l.description}</td>
                <td className="px-4 py-2.5 text-right text-slate-700">{fmtQty(l.quantity)} {l.uom}</td>
                <td className="px-4 py-2.5 text-right text-slate-700">{money(d.currency, l.unit_price)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-[#527D63]">{fmtQty(l.received_qty)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-[#A87F31]">{["in_transit", "partial"].includes(st) ? fmtQty(l.outstanding) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <StockReceiveDialog po={recv} onOpenChange={(v) => !v && setRecv(null)} onDone={reload} />
    </AppLayout>
  );
}
